/**
 * web/src/api/events.ts
 * Typed clients for /api/events/* (API-04). Server contract: src/api/events.ts.
 *
 * Routes:
 *   GET  /api/events            — listEvents()
 *   GET  /api/events/:id        — fetchEvent(id)
 *   POST /api/events (admin)    — createEvent(body)
 *   PUT  /api/events/:id (admin) — updateEvent(id, body); server invalidates
 *                                  the integration-factory cache on success.
 *   GET  /api/events/:id/health — fetchEventHealth(id) HealthAggregate
 *   GET/POST /api/events/:id/close-out (admin) — previewCloseOut / runCloseOut
 */
import { api } from '../lib/api';
import type { Event, EventFormat, HealthAggregate, OpsTemplateSection } from './types';

/**
 * Lists all events ordered by startDate desc.
 *
 * @returns Event[]
 * @throws ApiClientError on non-2xx
 * @sideEffects HTTP GET
 * @idempotent yes
 */
export async function listEvents(): Promise<Event[]> {
  return api<Event[]>('/api/events');
}

/**
 * Fetches one event.
 *
 * @param id event id
 * @returns Event
 * @throws ApiClientError(404) if not found
 * @sideEffects HTTP GET
 * @idempotent yes
 */
export async function fetchEvent(id: string): Promise<Event> {
  return api<Event>(`/api/events/${id}`);
}

/**
 * POST /api/events body. Mirrors server `CreateEventBody` Zod schema.
 * Required: name, code, format, startDate, endDate.
 * All other fields optional and accept null to clear.
 */
export interface CreateEventBody {
  name: string;
  code: string;
  format: EventFormat;
  startDate: string;
  endDate: string;
  inProgressDate?: string | null;
  optInDate?: string | null;
  closedWonStageId?: string | null;
  closedWonStageIds?: string[] | null;
  brandId?: string | null;
  hubspotPortalId?: string | null;
  sheetId?: string | null;
  driveRootFolderId?: string | null;
  driveLogosRootFolderId?: string | null;
  emailFromAddress?: string | null;
  mondaySpeakerBoardId?: string | null;
  mondaySalesBoardId?: string | null;
  slackWebhookUrl?: string | null;
  slackChannelName?: string | null;
  /**
   * Write-only plaintext Personatech API key (per-event). Stored encrypted at
   * rest server-side; never returned (read `Event.hasPersonatechApiToken`).
   * On update: a non-empty string replaces the stored key, `''` clears it, and
   * omitting the field leaves it unchanged.
   */
  personatechApiToken?: string | null;
  /**
   * Write-only plaintext Personatech PARTICIPANT LIST API key (per-event;
   * Personatech issues one key per ENDPOINT). Same semantics as
   * `personatechApiToken` — read `Event.hasPersonatechParticipantListApiToken`.
   */
  personatechParticipantListApiToken?: string | null;
  /**
   * Write-only plaintext Personatech PARTICIPANT SEARCH API key (per-event;
   * Personatech issues one key per ENDPOINT). Same semantics as
   * `personatechApiToken` — read `Event.hasPersonatechParticipantSearchApiToken`.
   */
  personatechParticipantSearchApiToken?: string | null;
  integrationConfig?: Record<string, unknown>;
  emailDryRun?: boolean;
  isActive?: boolean;
}

/** PUT /api/events/:id body. All fields optional (CreateEventBody.partial()). */
export type UpdateEventBody = Partial<CreateEventBody>;

/**
 * Admin-only. Updates an event. The server calls
 * `integrations/factory.invalidate(id)` on success so cached
 * IntegrationClients are dropped (D-1.3).
 *
 * @param id event id
 * @param body partial Event fields to update
 * @returns the updated Event row
 * @throws ApiClientError(403) if not admin;
 *         (404) if not found;
 *         (409) INVALID_STATE_TRANSITION on a code uniqueness conflict
 * @sideEffects HTTP PUT; server-side cache invalidation
 * @idempotent yes — same body produces the same final state
 */
export async function updateEvent(id: string, body: UpdateEventBody): Promise<Event> {
  return api<Event>(`/api/events/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Admin-only. Creates a new event. Server validates `code` uniqueness and
 * throws INVALID_STATE_TRANSITION (409) on conflict. integrationConfig is
 * stored verbatim on the new row.
 *
 * @param body event fields
 * @returns the created Event row
 * @throws ApiClientError(403) if not admin;
 *         (400) VALIDATION_ERROR with details.fieldErrors;
 *         (409) INVALID_STATE_TRANSITION on duplicate code
 * @sideEffects HTTP POST
 * @idempotent no — creates a new row each call
 */
export async function createEvent(body: CreateEventBody): Promise<Event> {
  return api<Event>('/api/events', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Returns the per-integration health-check aggregate for the event.
 * Each value is `{ ok, latencyMs, message?, details? }` — never raw SDK
 * error bodies (T-04-08-05).
 *
 * @param id event id
 * @returns HealthAggregate keyed by integration name
 * @throws ApiClientError(404) if event not found;
 *         (500) if a required Event field is null (config error)
 * @sideEffects HTTP GET; server runs all integration probes in parallel
 * @idempotent yes
 */
export async function fetchEventHealth(id: string): Promise<HealthAggregate> {
  return api<HealthAggregate>(`/api/events/${id}/health`);
}

/** Response of GET (preview) / POST (run) /api/events/:id/close-out. */
export interface CloseOutResult {
  isOver: boolean;
  /** Gates waiting for approval (or rejected) that get cleared. */
  approvalsCleared: number;
  /** Every open step cleared (approvals + waits for a reply/date). */
  stepsSkipped: number;
  /** Open tracked emails (pending confirmations) marked CLOSED. */
  confirmationsClosed: number;
  workflowsCancelled: number;
  /** Speaker workflows kept open for their post-event thank-you. */
  thankYousKept: number;
  /** Workflows mid-step during the run — left alone; run again shortly. */
  busyWorkflowIds: string[];
}

/**
 * Admin-only. Previews what "close out event" would clear. Read-only.
 *
 * @param id event id
 * @returns CloseOutResult counts (nothing written)
 * @throws ApiClientError(403) if not admin; (404) if event not found
 * @sideEffects HTTP GET
 * @idempotent yes
 */
export async function previewCloseOut(id: string): Promise<CloseOutResult> {
  return api<CloseOutResult>(`/api/events/${id}/close-out`);
}

/**
 * Admin-only. Clears every pending approval + confirmation on an event that
 * is over, leaving speaker thank-yous open. Sends nothing.
 *
 * @param id event id
 * @returns CloseOutResult counts of what was cleared
 * @throws ApiClientError(409) EVENT_NOT_OVER before the event's final day
 *         has passed; (403) if not admin; (404) if event not found
 * @sideEffects HTTP POST; server skips steps, closes tracked emails, cancels
 *              finished workflows
 * @idempotent yes — a second call clears nothing
 */
export async function runCloseOut(id: string): Promise<CloseOutResult> {
  return api<CloseOutResult>(`/api/events/${id}/close-out`, { method: 'POST' });
}

/** Response of POST /api/events/:id/promotion-release. */
export interface PromotionReleaseResult {
  /** Naive-UTC ISO timestamp stamped on integrationConfig.promotionReleasedAt. */
  promotionReleasedAt: string;
  /** How many active speaker workflows had their promotion hold released. */
  workflowsReleased: number;
}

/**
 * Admin-only. Marks the event's speakers as announced: stamps
 * `integrationConfig.promotionReleasedAt` and releases the promotion hold on
 * every active speaker workflow. One-way — there is no un-announce.
 *
 * @param id event id
 * @returns { promotionReleasedAt, workflowsReleased }
 * @throws ApiClientError(403) if not admin; (404) if event not found;
 *         (409) INVALID_STATE_TRANSITION when already stamped — the existing
 *         timestamp rides in `details.promotionReleasedAt`
 * @sideEffects HTTP POST; server stamps the event + fires workflow events
 * @idempotent no — a second call 409s with the original timestamp
 */
export async function announcePromotion(id: string): Promise<PromotionReleaseResult> {
  return api<PromotionReleaseResult>(`/api/events/${id}/promotion-release`, { method: 'POST' });
}

/* -------------------------------------------------------------------------- */
/* Ops next-steps template ingestion (GET + admin sync)                       */
/* -------------------------------------------------------------------------- */

/** Ingest status of a stored ops template source row. `skipped` only appears
 *  in a sync result (the doc was unconfigured — no row is persisted). */
export type TemplateSourceStatus = 'ok' | 'review' | 'stale' | 'error' | 'skipped';

/**
 * One stored ops next-steps template source (GET /api/events/:id/templates):
 * a source Doc ingested into pickable sections plus its freshness status.
 * `message` carries LLM-flagged sectioning issues or a fetch error; the
 * verbatim section bodies are omitted (the gate composes them).
 */
export interface TemplateSourceView {
  id: string;
  docKey: string;
  status: TemplateSourceStatus;
  sectionCount: number;
  sourceDocId: string;
  sourceRevisionId: string | null;
  message: string | null;
  ingestedAt: string | null;
  ingestedByName: string | null;
  updatedAt: string | null;
  sections: OpsTemplateSection[];
}

/** Per-doc outcome from POST /api/events/:id/templates/sync. */
export interface TemplateSyncResult {
  docKey: string;
  status: TemplateSourceStatus;
  sectionCount: number;
  /** LLM-flagged sectioning issues (non-fatal — the row is still usable). */
  issues: string[];
  /** Fetch/ingest error message when status === 'error', else null. */
  error: string | null;
}

/**
 * Lists the stored ops template sources for an event (the ingest result the
 * gate picks from). Read-only; any authed user.
 *
 * @param id event id
 * @returns TemplateSourceView[] (one per ingested ops Doc)
 * @throws ApiClientError(404) if event not found
 * @sideEffects HTTP GET
 * @idempotent yes
 */
export async function fetchEventTemplates(id: string): Promise<TemplateSourceView[]> {
  const res = await api<{ templates: TemplateSourceView[] }>(`/api/events/${id}/templates`);
  return res.templates;
}

/**
 * Admin-only. Re-ingests the event's ops template Docs into stored sections
 * (fetch → Claude section review → upsert). Idempotent — re-syncing the same
 * Docs yields the same sections and flips a stale row back to ok.
 *
 * @param id event id
 * @returns per-doc sync summary (status + sectionCount + issues/error)
 * @throws ApiClientError(403) if not admin; (404) if event not found
 * @sideEffects HTTP POST; fetches Docs + Anthropic calls; upserts TemplateSource rows
 * @idempotent yes
 */
export async function syncEventTemplates(id: string): Promise<TemplateSyncResult[]> {
  const res = await api<{ results: TemplateSyncResult[] }>(`/api/events/${id}/templates/sync`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return res.results;
}
